// Copyright (c) 2019 Deere & Company

import React, {PropTypes} from 'react';
import {
    getRepairedDateErrorMessage,
    getVsrMessage,
    hasVsrErrorForClaimField,
    isRepairDateAfterFailureDate,
    isResponsibleAccountEditable
} from '../../../shared/services/claim-shared-helper';
import AmountOfUseField from '../../../shared/components/claim-fields/amount-of-use';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import ClaimRgaStatusIndicator from '../../../shared/components/claim-rga-status-indicator';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import {FormattedMessage} from 'react-intl';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import LabeledCheckboxWithError from '../../../shared/components/claim-fields/labeled-checkbox-with-error';
import LabeledDateInput from '../../../shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../shared/components/claim-fields/labeled-input-with-blur';
import LabeledKeyValueDropDown from '../../../shared/components/claim-fields/labeled-key-value-dropdown';
import Product from '@deere/cps-it.warranty-model-factory/lib/product';
import ResponsibleAccounts from '../../../shared/components/claim-fields/responsible-accounts';
import WearPartsClaim from '@deere/cps-it.warranty-model-factory/lib/claim-wear-part';
import {fixCursorPosition} from '../../../../shared/services/fix-cursor-position';
import {formatPin} from '../../../../shared/services/format-pin';
import {isValidClaim} from '../../services/claim-validation-helper';
import {isValidDate} from '../../../../shared/services/build-date';
import moment from 'moment';

const getTranslationId = remanIndicator =>
    remanIndicator ? 'YES_LOWERCASE' : 'NO_LOWERCASE';

function resetCustomerProductDetails() {

    this.actions.resetCustomerDetails();
    this.actions.resetCkcCustomerFailure();
    this.actions.resetProductDetails();

}

function handlePinBlur(pin) {

    this.actions.updateWearPartsClaimInputOnBlur('pin', pin);

    if (pin) {

        if (pin !== this.product.pin) {

            this::resetCustomerProductDetails();
            this.actions.readClaimProduct(pin, true);

        }

    } else {

        this::resetCustomerProductDetails();

    }

}

function handleInputBlur(property, value) {

    this.actions.updateWearPartsClaimInputOnBlur(property, value, this.claim);

}

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccountsDropDown(accountId, this.selectedAccounts, this.claim.pin);

}

function handleUseAmountChange(amount, index) {

    const indexToChange = this.product.complianceApplies ? index : 0;

    this.actions.updateWearPartsClaimUseAmount(amount, indexToChange);

}

function handleUnitOptionChange(key) {

    this.actions.updateWearPartsClaimUnitDropDown(key, this.product.useAmountAllowedUnits);

}

function handleWorkOrderOneChange(workOrder) {

    this.actions.updateWearPartsClaimWorkOrderOne(workOrder);

}

function handleFailurePartChange(partNumber) {

    this.actions.updateWearPartsClaimFailurePart(partNumber);

}

function handleFailureRemanIndicatorChange(selected) {

    this.actions.updateWearPartsClaimFailureRemanIndicator(selected);

}

function handleFailureDateChange(date) {

    this.actions.updateWearPartsClaimFailureDate(date);

}

function handleRepairDateChange(date) {

    this.actions.updateWearPartsClaimRepairDate(date);

}

function handleButtonClick() {

    const action = new ClaimAction({
        actionName: 'VALIDATE',
        id: 'VALIDATE',
        mode: ''
    });

    this.actions.updateSelectedAction(action);

}

function handleOwnershipStatusChange(event) {

    this.actions.updateOwnershipStatusDropDown(event.target.value, this.ownershipStatus);

}

function handleDropDownBlur(property, event) {

    this.actions.updateWearPartsClaimInputOnBlur(property, event.target.value);

}

function handleWorkOrderTwoChange(workOrder) {

    this.actions.updateWearPartsClaimWorkOrderTwo(workOrder);

}

function handleRepairNumberChange(repairNumber) {

    this.actions.updateWearPartsClaimRepairNumber(repairNumber);

}

function handleDateBlur(property, date) {

    const modifiedDate = isValidDate(date.target.value) ? moment.utc(date.target.value).valueOf() : null;

    this.actions.updateWearPartsClaimInputOnBlur(property, modifiedDate, this.claim);

}

function handlePinChange(pin, event) {

    const formattedPin = formatPin(pin);

    this.actions.updateWearPartsClaimPin(formattedPin);
    this.actions.updateNonTrackedPinModalPin(formattedPin);
    fixCursorPosition(event, formattedPin);

}

const ClaimDetailsContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='claim-details-section'>
            <div className='claim-details-section-header'>
                <h3>
                    <FormattedMessage id='PRODUCT_INFORMATION'/>
                </h3>
                {props.claim.rgaStatus.status && props.claim.rgaStatus.status !== 'NONE' &&
                    <ClaimRgaStatusIndicator
                        claim={props.claim.claimNumber}
                        claimRgaStatus={props.claim.rgaStatus}
                    />
                }
            </div>
            <div className='product-info'>
                <ResponsibleAccounts
                    dealers={props.selectedAccounts}
                    errorMessage='DEALER_LOCATION_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                    isEditable={canEditResponsibleAccount}
                    isValid={props.formValidation.responsibleAccount}
                    onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                    onOptionChange={props::handleResponsibleAccountChange}
                    selectedAccount={props.claim.responsibleAccount}
                    vsrMessage={getVsrMessage(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                />
                <LabeledInputWithBlur
                    className='pin'
                    errorMessage='PIN_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pin', props.isEditable)}
                    isEditable={props.isEditable && (props.claim.claimStatus.key === 'ZDFT' || !props.claim.claimStatus.key)}
                    isValid={props.formValidation.pin}
                    label='PIN'
                    maxLength={18}
                    onBlur={props::handlePinBlur}
                    onChange={props::handlePinChange}
                    value={props.claim.pin}
                    vsrMessage={getVsrMessage(props.claim.errors, 'pin', props.isEditable)}
                />
            </div>

            <h3>
                <FormattedMessage id='REFERENCE_INFORMATION'/>
            </h3>
            <div className='reference-info'>
                <LabeledInputWithBlur
                    className='work-order1'
                    errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.workOrderOne}
                    label='WORK_ORDER_ONE'
                    maxLength={26}
                    onBlur={handleInputBlur.bind(props, 'workOrderOne')}
                    onChange={props::handleWorkOrderOneChange}
                    value={props.claim.referenceInfo.referenceNumber1}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                />
                <LabeledInput
                    className='work-order2'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderTwo', props.isEditable)}
                    isEditable={props.isEditable}
                    label='WORK_ORDER_TWO'
                    maxLength={12}
                    onChange={props::handleWorkOrderTwoChange}
                    value={props.claim.referenceInfo.referenceNumber2}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderTwo', props.isEditable)}
                />
                <LabeledInput
                    className='repair-number'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    label='REPAIR_NUMBER'
                    maxLength={13}
                    onChange={props::handleRepairNumberChange}
                    value={props.claim.referenceInfo.repairNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairNumber', props.isEditable)}
                />
                <LabeledKeyValueDropDown
                    className='ownership-status'
                    errorMessage='OWNERSHIP_STATUS_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'ownershipStatus', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.ownershipStatus}
                    label='OWNERSHIP_STATUS'
                    list={props.ownershipStatus}
                    onBlur={handleDropDownBlur.bind(props, 'ownershipStatus')}
                    onChange={props::handleOwnershipStatusChange}
                    selectedValue={props.claim.referenceInfo.ownershipStatus}
                    vsrMessage={getVsrMessage(props.claim.errors, 'ownershipStatus', props.isEditable)}
                />
            </div>
            <div className='failure-info'>
                <LabeledDateInput
                    className='failure-date'
                    date={props.claim.failureInfo.failureDate}
                    errorMessage='FAILED_DATE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failureDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failureDate}
                    label='FAILED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'failureDate')}
                    onDateChange={props::handleFailureDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failureDate', props.isEditable)}
                />
                <LabeledDateInput
                    className='repair-date'
                    date={props.claim.failureInfo.repairDate}
                    errorMessage={getRepairedDateErrorMessage(props.claim.failureInfo.repairDate)}
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.repairDate && isRepairDateAfterFailureDate(props.claim.failureInfo.repairDate, props.claim.failureInfo.failureDate)}
                    label='REPAIRED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'repairDate')}
                    onDateChange={props::handleRepairDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairDate', props.isEditable)}
                />

                <AmountOfUseField
                    allowedUnits={props.product.useAmountAllowedUnits}
                    className='amount-of-use'
                    complianceApplies={props.product.complianceApplies}
                    errors={props.claim.errors}
                    formValidation={props.formValidation}
                    isEditable={props.isEditable}
                    onInputBlur={handleInputBlur.bind(props, 'useAmount')}
                    onInputChange={props::handleUseAmountChange}
                    onOptionBlur={handleInputBlur.bind(props, 'useAmountUnit')}
                    onOptionChange={props::handleUnitOptionChange}
                    useAmounts={props.claim.useAmounts}
                />
                <LabeledInputWithBlur
                    className='failure-part'
                    errorMessage='FAILED_PART_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failurePart', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failurePart}
                    label='FAILED_PART'
                    maxLength={18}
                    onBlur={handleInputBlur.bind(props, 'failurePart')}
                    onChange={props::handleFailurePartChange}
                    value={props.claim.failureInfo.failurePartNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failurePart', props.isEditable)}
                />

                {props.claim.failureInfo.showRemanIndicator &&
                    <LabeledCheckboxWithError
                        className='reman-indicator'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'remanIndicator', props.isEditable)}
                        id='FAILED_PART_IS_A_REMAN_PART'
                        isEditable={props.isEditable}
                        isSelected={props.claim.failureInfo.remanIndicator}
                        label='FAILED_PART_IS_A_REMAN_PART'
                        onChange={props::handleFailureRemanIndicatorChange}
                        translationId={getTranslationId(props.claim.failureInfo.remanIndicator)}
                        vsrMessage={getVsrMessage(props.claim.errors, 'remanIndicator', props.isEditable)}
                    />
                }
            </div>
            {(props.isEditable && props.claim.showValidate) &&
                <div className='button-group'>
                    <button
                        disabled={!isValidClaim(props.claim)}
                        onClick={props::handleButtonClick}
                    >
                        <FormattedMessage id='VALIDATE'/>
                    </button>
                </div>
            }
        </section>
    );

};

ClaimDetailsContainer.displayName = 'ClaimDetailsContainer';
ClaimDetailsContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(WearPartsClaim).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    isEditable: PropTypes.bool.isRequired,
    isValidClaim: PropTypes.bool.isRequired,
    ownershipStatus: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    product: PropTypes.instanceOf(Product).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired
};

export default ClaimDetailsContainer;



// WEBPACK FOOTER //
// ./src/claim/wear-parts/components/claim-details-section/claim-details-container.js